/**
 * DeadlinesByCountry — one-glance summary chip row grouping the
 * shortlist's earliest upcoming deadline by country. Sits above the
 * per-degree DeadlineTracker so a parent can scan
 *   "🇬🇧 UK · 25 Jan 2027  ·  🇺🇸 US · 1 Jan 2027  ·  🇮🇪 Ireland · 1 Feb 2027"
 * before drilling into individual courses.
 *
 * Rules:
 *   • One chip per country present on the shortlist.
 *   • Date is the earliest still-visible `application_deadline`
 *     (falling back to `application.closes`) across that country's
 *     shortlisted degrees. Past deadlines are shown too, greyed out.
 *   • Countries are sorted by that earliest date (soonest first) so
 *     the urgent cluster reads left-to-right.
 *   • Chip tint mirrors the DeadlineTracker urgency palette:
 *       ≤30d red · ≤90d amber · else emerald · past = neutral strikethrough.
 *   • Renders `null` when nothing on the shortlist has a dated
 *     deadline (empty state handled by the tracker below).
 */
import { useMemo } from "react";
import { CalendarClock } from "lucide-react";

// Map free-text country string → { name, cc } for chip formatting.
// Keys are lowercased so we can be tolerant of "United Kingdom" vs "uk".
const COUNTRY_META = {
  "united kingdom":     { name: "UK",           cc: "GB" },
  "britain":            { name: "UK",           cc: "GB" },
  "uk":                 { name: "UK",           cc: "GB" },
  "great britain":      { name: "UK",           cc: "GB" },
  "ireland":            { name: "Ireland",      cc: "IE" },
  "republic of ireland":{ name: "Ireland",      cc: "IE" },
  "united states":      { name: "US",           cc: "US" },
  "united states of america": { name: "US",     cc: "US" },
  "usa":                { name: "US",           cc: "US" },
  "us":                 { name: "US",           cc: "US" },
  "germany":            { name: "Germany",      cc: "DE" },
  "france":             { name: "France",       cc: "FR" },
  "netherlands":        { name: "Netherlands",  cc: "NL" },
  "the netherlands":    { name: "Netherlands",  cc: "NL" },
  "denmark":            { name: "Denmark",      cc: "DK" },
  "sweden":             { name: "Sweden",       cc: "SE" },
  "norway":             { name: "Norway",       cc: "NO" },
  "finland":            { name: "Finland",      cc: "FI" },
  "australia":          { name: "Australia",    cc: "AU" },
  "canada":             { name: "Canada",       cc: "CA" },
  "new zealand":        { name: "New Zealand",  cc: "NZ" },
  "singapore":          { name: "Singapore",    cc: "SG" },
  "hong kong":          { name: "Hong Kong",    cc: "HK" },
  "japan":              { name: "Japan",        cc: "JP" },
  "korea, south":       { name: "S. Korea",     cc: "KR" },
  "south korea":        { name: "S. Korea",     cc: "KR" },
  "china":              { name: "China",        cc: "CN" },
  "india":              { name: "India",        cc: "IN" },
  "united arab emirates": { name: "UAE",        cc: "AE" },
  "uae":                { name: "UAE",          cc: "AE" },
  "czechia":            { name: "Czechia",      cc: "CZ" },
  "czech republic":     { name: "Czechia",      cc: "CZ" },
  "portugal":           { name: "Portugal",     cc: "PT" },
  "spain":              { name: "Spain",        cc: "ES" },
  "italy":              { name: "Italy",        cc: "IT" },
  "switzerland":        { name: "Switzerland",  cc: "CH" },
  "austria":            { name: "Austria",      cc: "AT" },
  "belgium":            { name: "Belgium",      cc: "BE" },
  "poland":             { name: "Poland",       cc: "PL" },
  "taiwan":             { name: "Taiwan",       cc: "TW" },
  "israel":             { name: "Israel",       cc: "IL" },
};

const _MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  try {
    const cp = [...cc.toUpperCase()].map((ch) => 0x1F1E6 + ch.charCodeAt(0) - 65);
    return String.fromCodePoint(...cp);
  } catch {
    return "";
  }
}

function parseISO(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T23:59:59Z`);
  return isNaN(d.getTime()) ? null : d;
}

function fmt(d) {
  return `${d.getUTCDate()} ${_MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function resolveCountry(deg, uni) {
  const raw = (deg?.country || uni?.country || "").trim();
  const key = raw.toLowerCase();
  if (COUNTRY_META[key]) return COUNTRY_META[key];
  // Fallback: try `country_code` explicitly if the free-text lookup
  // missed. Also handles inputs like "GB" or "US".
  const cc = (deg?.country_code || uni?.country_code || "").toUpperCase();
  if (cc && cc.length === 2) return { name: raw || cc, cc: cc === "GB" ? "GB" : cc };
  if (!raw) return null;
  return { name: raw, cc: "" };
}

export default function DeadlinesByCountry({ items, testId = "deadlines-by-country" }) {
  const now = new Date();
  const groups = useMemo(() => {
    const map = new Map(); // key = display name, val = { name, cc, earliest, count }
    for (const it of items || []) {
      const deg = it?.degree;
      if (!deg) continue;
      const meta = resolveCountry(deg, it.university);
      if (!meta) continue;
      const dl = deg.application_deadline || deg?.application?.closes || null;
      const parsed = parseISO(dl);
      if (!parsed) continue;
      const key = meta.name;
      const cur = map.get(key) || { ...meta, count: 0, earliest: null };
      cur.count += 1;
      if (!cur.earliest || parsed < cur.earliest) cur.earliest = parsed;
      map.set(key, cur);
    }
    return Array.from(map.values())
      .filter((g) => g.earliest)
      .sort((a, b) => a.earliest - b.earliest);
  }, [items]);

  if (groups.length === 0) return null;

  return (
    <div
      data-testid={testId}
      className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-stone-200 bg-white px-4 py-3"
    >
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500 font-semibold shrink-0 mr-1">
        <CalendarClock className="h-3.5 w-3.5" />
        By country
      </div>
      {groups.map((g) => {
        const days = Math.ceil((g.earliest.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const past = days < 0;
        const tint = past
          ? "bg-stone-50 border-stone-200 text-stone-500 line-through"
          : days <= 30
            ? "bg-red-50 border-red-200 text-red-800"
            : days <= 90
              ? "bg-amber-50 border-amber-200 text-amber-900"
              : "bg-emerald-50 border-emerald-200 text-emerald-800";
        const flag = flagEmoji(g.cc);
        const title = past
          ? `${g.count} course${g.count === 1 ? "" : "s"} · earliest deadline ${fmt(g.earliest)} (past)`
          : `${g.count} course${g.count === 1 ? "" : "s"} · earliest deadline ${fmt(g.earliest)} · in ${days}d`;
        return (
          <span
            key={g.name}
            data-testid={`${testId}-${(g.cc || g.name).toLowerCase().replace(/\s+/g, "-")}`}
            className={`inline-flex items-center gap-1.5 rounded-full border ${tint} text-[11px] font-medium px-2.5 py-1`}
            title={title}
          >
            {flag && <span aria-hidden>{flag}</span>}
            <span className="font-semibold">{g.name}</span>
            <span aria-hidden>·</span>
            <span>{fmt(g.earliest)}</span>
            {g.count > 1 && (
              <span className="text-stone-500 font-normal">×{g.count}</span>
            )}
          </span>
        );
      })}
    </div>
  );
}
